import type {
  ApiListingRequest,
  CardInfoProps,
  Columns,
  TableToolbarFilterConfig,
} from '@bitstopco/bitstop-theme';
import { CONSTANTS, ClickableText, TableColumnPie } from '@bitstopco/bitstop-theme';

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MonitorIcon from '@mui/icons-material/Monitor';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { type ChipProps, Stack } from '@mui/material';

import MachinesDownIcon from '@/assets/machines-down-icon.svg?react';
import MachinesAtms from '@/components/machines/tabs/atms/MachinesAtms';
import MachinesStatus from '@/components/machines/tabs/atms/tableColumns/MachinesStatus';
import MachinesCashLogs from '@/components/machines/tabs/cashLogs/MachinesCashLogs';
import MachinesCashLogsBills from '@/components/machines/tabs/cashLogs/tableColumns/MachinesCashLogsBills';
import AssetBadge from '@/components/ui/AssetBadge';
import TagList from '@/components/ui/TagList';
import TableColumnCashLogMachine from '@/components/ui/table/TableColumnCashLogMachine';
import TableColumnDate from '@/components/ui/table/TableColumnDate';
import TableColumnIncludedLocation from '@/components/ui/table/TableColumnIncludedLocation';
import TableColumnMachine from '@/components/ui/table/TableColumnMachine';
import TableHeaderTooltipIcon from '@/components/ui/table/TableHeaderDateTooltipIcon';

import { validateFilterById } from '@/helpers/api';

import { US_STATE_FILTER_OPTIONS } from './usStates';

import { DEFAULT_MACHINE_TIME_LEGEND } from './date';

import CashLogProvenance from '@/components/machines/tabs/cashLogs/tableColumns/CashLogProvenance';
import CashLogStatus from '@/components/machines/tabs/cashLogs/tableColumns/CashLogStatus';
import type { InstalledMachine, Machine, MachinesCashLog, WithTags } from '@/types';

/**
 * Machines Panels configuration (Tabs)
 * * PANELS: tab names
 * * PANELS_ROUTES: tab routes
 * * PANELS_COMPONENTS: tab related components
 */
export const getMachineTabsConfig = (integrations = false) => {
  return {
    PANELS: ['Machines', 'Cash Logs', ...(integrations ? ['Integrations'] : [])],
    PANELS_ROUTES: [
      '/machines/atms',
      '/machines/cash-logs',
      ...(integrations ? ['/machines/integrations'] : []),
    ],
    PANELS_COMPONENTS: [MachinesAtms, MachinesCashLogs, ...(integrations ? [MachinesAtms] : [])],
  };
};

/**
 * Machines top card configuration
 */
export const MACHINES_CARDS = {
  total_online: {
    title: 'Machines Online',
    icon: MonitorIcon,
    showFloatingIcon: false,
  },
  total_down: {
    title: 'Machines down',
    icon: MonitorIcon,
    showFloatingIcon: true,
    floatingIcon: (() => <MachinesDownIcon />) as unknown as CardInfoProps['floatingIcon'],
  },
  total_machines: {
    title: 'Total Machines',
    icon: SyncAltIcon,
    showFloatingIcon: false,
  },
  cash_total: {
    title: 'Cash Total',
    icon: AttachMoneyIcon,
    showFloatingIcon: false,
  },
};

/**
 * Machines columns configuration: The columns configuration will be used to generate the table with the desired data
 * * You can learn more about the Table columns configuration in the Theme Live docs: https://github.com/bitstopco/bitstop-theme#live-docs
 */
export const MACHINES_COLUMNS: Columns<WithTags<Machine>> = {
  defaultValue: '-',
  labelSx: {
    whiteSpace: 'nowrap',
  },
  valueSx: {
    whiteSpace: 'nowrap',
  },
  items: [
    {
      value: 'id',
      label: 'Machine',
      valueMapper: (_, data) => <TableColumnMachine data={data} />,
      valueSx: {
        '& h6': {
          minWidth: 300,
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      },
    },
    {
      value: 'location_id',
      label: 'Location',
      valueMapper: (_, data) => <TableColumnIncludedLocation link data={data} />,
    },
    {
      value: 'status',
      label: 'Status',
      valueMapper: (value: Machine['status']) => value && <MachinesStatus value={value} />,
    },
    {
      value: 'currency',
      label: 'Supported Currency',
      valueMapper: (value: Machine['currency']) => value ?? '-',
    },
    {
      value: 'serial',
      label: 'Serial',
    },
    {
      value: 'pick_up_id',
      label: 'Pickup ID',
    },
    {
      value: 'enabled_coin_ids',
      label: 'Supported Assets',
      hideSort: true,
      valueSx: { minWidth: 320 },
      valueMapper: (_, data: Machine) => {
        return (
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {data.enabled_coins?.length === 0 && '-'}
            {data.enabled_coins?.map((coin, idx) => (
              <AssetBadge key={idx} coin={coin} />
            ))}
          </Stack>
        );
      },
    },
    {
      value: 'live_cash',
      label: 'Cash Total',
      valueMapper: (value: Machine['live_cash'] = 0) => `$${value}`,
      valueSx: {
        textAlign: 'right',
      },
    },
    {
      value: 'live_total_amount_of_bills',
      label: 'Cash Box',
      valueMapper: (value: Machine['live_total_amount_of_bills'], data) => (
        <TableColumnPie {...{ value: value ?? undefined, data }} />
      ),
    },
    {
      label: 'Tags',
      labelSx: {
        minWidth: '250px',
      },
      valueMapper: (_, { tag_ids, tags }) => (
        <TagList selectedTagIds={tag_ids} tagsCollection={tags} />
      ),
      hideSort: true,
    },
    {
      value: 'last_restart',
      label: 'Last Restart',
      valueMapper: (_, data) => data?.last_restart_humanized,
    },
    {
      value: 'last_seen',
      label: 'Last Seen',
      valueMapper: (_, data) => data?.last_seen_humanized,
    },
    {
      value: 'last_sale_created_at',
      label: 'Last Sale',
      valueMapper: (_, data) => data?.last_sale_created_at_humanized,
    },
  ],
};

/**
 * Integration Machines columns configuration: The columns configuration will be used to generate the table with the desired data
 * * You can learn more about the Table columns configuration in the Theme Live docs: https://github.com/bitstopco/bitstop-theme#live-docs
 */
export const INTEGRATION_MACHINES_COLUMNS: Columns<WithTags<Machine>> = {
  defaultValue: '-',
  labelSx: {
    whiteSpace: 'nowrap',
  },
  valueSx: {
    whiteSpace: 'nowrap',
  },
  items: [
    {
      value: 'id',
      label: 'Machine',
      valueMapper: (_, data) => <TableColumnMachine data={data} />,
      valueSx: {
        '& h6': {
          maxWidth: 400,
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      },
    },
    {
      value: 'location_id',
      label: 'Location',
      valueMapper: (_, data) => <TableColumnIncludedLocation link data={data} />,
    },
    {
      value: 'currency',
      label: 'Supported Currency',
      valueMapper: (value: Machine['currency']) => value ?? '-',
    },
    {
      value: 'enabled_coin_ids',
      label: 'Supported Assets',
      hideSort: true,
      valueSx: { minWidth: 320 },
      valueMapper: (_, data: Machine) => {
        return (
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {data.enabled_coins?.length === 0 && '-'}
            {data.enabled_coins?.map((coin, idx) => (
              <AssetBadge key={idx} coin={coin} />
            ))}
          </Stack>
        );
      },
    },
    {
      label: 'Tags',
      labelSx: {
        minWidth: '250px',
      },
      valueMapper: (_, { tag_ids, tags }) => (
        <TagList selectedTagIds={tag_ids} tagsCollection={tags} />
      ),
      hideSort: true,
    },
    {
      value: 'last_sale_created_at',
      label: 'Last Sale',
      valueMapper: (_, data) => data?.last_sale_created_at_humanized,
    },
  ],
};

/**
 * Global Cash Logs columns configuration: The columns configuration will
 * be used to generate the table with the desired data
 */
export const GLOBAL_CASH_LOGS_COLUMNS: Columns<MachinesCashLog> = {
  defaultValue: '-',
  labelSx: { whiteSpace: 'nowrap' },
  valueSx: { whiteSpace: 'nowrap' },
  items: [
    {
      value: 'id',
      label: 'Clear ID',
      valueMapper: (value: number) =>
        value && (
          <ClickableText name="ID" text={value.toString()} clipboardValue={value.toString()} />
        ),
    },
    {
      value: 'atm_id',
      label: 'Machine',
      valueMapper: (_, data) => <TableColumnCashLogMachine link data={data} />,
    },
    {
      value: 'location_id',
      label: 'Location',
      valueMapper: (_, data) => <TableColumnIncludedLocation link data={data} />,
    },
    {
      value: 'status',
      label: 'Status',
      valueMapper: (value) => value && <CashLogStatus value={value} />,
    },
    {
      value: 'provenance',
      label: 'Provenance',
      valueMapper: (value) => value && <CashLogProvenance value={value} />,
    },
    {
      value: 'created_at',
      label: 'Pulled On (UTC)',
      labelMapper: (value: string) => (
        <TableHeaderTooltipIcon label={value} tooltipTitle={DEFAULT_MACHINE_TIME_LEGEND} />
      ),
      valueMapper: (value: MachinesCashLog['created_at'], { local_created_at, local_time_zone }) =>
        value && (
          <TableColumnDate
            value={value}
            options={{
              timeZone: 'UTC',
            }}
            auxiliaryDate={{
              value: local_created_at,
              options: {
                timeZone: local_time_zone,
                normalizeAsUTC: false,
                performUtcToZonedTime: false,
              },
            }}
          />
        ),
    },
    {
      value: 'tx_count',
      label: 'Transactions',
      valueSx: { textAlign: 'right' },
    },
    {
      value: 'total_worth_of_bills',
      label: 'Bills Worth',
      valueMapper: (value: number) => `$${value}`,
      valueSx: { textAlign: 'right', fontWeight: 500 },
    },
    {
      value: 'total_amount_of_bills',
      label: 'Breakdown',
      hideSort: true,
      valueMapper: (value, data) => <TableColumnPie {...{ value, data }} />,
    },
  ],
};

/**
 * Cash Logs filters configuration
 */
export const CASH_LOGS_FILTERS_CONFIG: TableToolbarFilterConfig[] = [
  {
    type: 'select-ids',
    property: 'atm_id',
    placeholder: 'Machine',
    inputPlaceholder: 'Machine ID',
    label: 'Machine',
    mapperSelectedValue: (value: string) => `Machine ${value}`,
    mapperSelectedValues: (count: number) => `Machines (${count})`,
    validate: validateFilterById('atm'),
  },
  {
    type: 'select',
    property: 'status',
    placeholder: 'Status',
    label: 'Status',
    showFooter: true,
    hideApplyButton: true,
    clearText: 'Clear filter',
    options: [
      { value: 'provisional', label: 'Provisional' },
      { value: 'confirmed', label: 'Confirmed' },
      { value: 'invalidated', label: 'Invalidated' },
      { value: 'deposited', label: 'Deposited' },
      { value: 'deposited_over', label: 'Deposited Over' },
      { value: 'deposited_under', label: 'Deposited Under' },
      { value: 'voided', label: 'Voided' },
    ],
  },
  {
    type: 'select',
    property: 'provenance',
    placeholder: 'Provenance',
    label: 'Provenance',
    showFooter: true,
    hideApplyButton: true,
    clearText: 'Clear filter',
    // `heartbeat_inferred` is intentionally omitted until backend confirms it is
    // still populated by any service (see DEV-1611 grooming notes).
    options: [
      { value: 'machine_reported', label: 'Machine' },
      { value: 'manual', label: 'Manual' },
    ],
  },
];

/**
 * Cash Logs columns configuration: The columns configuration will be used to generate the table with the desired data
 * * You can learn more about the Table columns configuration in the Theme Live docs: https://github.com/bitstopco/bitstop-theme#live-docs
 */
export const CASH_LOGS_COLUMNS: Columns<MachinesCashLog> = {
  defaultValue: '-',
  labelSx: {
    whiteSpace: 'nowrap',
  },
  valueSx: {
    whiteSpace: 'nowrap',
  },
  items: [
    {
      value: 'id',
      label: 'Cash Log ID',
      valueMapper: (value: MachinesCashLog['id']) =>
        value && (
          <ClickableText name="ID" text={value.toString()} clipboardValue={value.toString()} />
        ),
    },
    {
      value: 'atm_id',
      label: 'Machine',
      valueMapper: (_, data) => <TableColumnCashLogMachine link data={data} />,
    },
    {
      value: 'atm_id',
      label: 'Location',
      valueMapper: (_, data) => <TableColumnIncludedLocation link data={data} />,
    },
    {
      value: 'created_at',
      label: 'Pulled On',
      labelMapper: (value: string) => (
        <TableHeaderTooltipIcon label={value} tooltipTitle={DEFAULT_MACHINE_TIME_LEGEND} />
      ),
      valueMapper: (value: MachinesCashLog['created_at'], { local_created_at, local_time_zone }) =>
        value && (
          <TableColumnDate
            value={value}
            options={{
              timeZone: 'UTC',
            }}
            auxiliaryDate={{
              value: local_created_at,
              options: {
                timeZone: local_time_zone,
                normalizeAsUTC: false,
                performUtcToZonedTime: false,
              },
            }}
          />
        ),
    },
    {
      value: 'total_worth_of_bills',
      label: 'Bills Worth',
      valueMapper: (value: MachinesCashLog['total_worth_of_bills'] = 0) => `$${value}`,
      labelSx: {
        textAlign: 'center',
        whiteSpace: 'nowrap',
      },
      valueSx: {
        textAlign: 'center',
        whiteSpace: 'nowrap',
        pr: 5,
      },
    },
    {
      value: 'one',
      label: 'Bills',
      hideSort: true,
      valueMapper: (value: MachinesCashLog['one'], data) => (
        <MachinesCashLogsBills {...{ value: value?.toString(), data }} />
      ),
    },
  ],
};

export const MACHINE_CASH_LOGS_COLUMNS: Columns<MachinesCashLog> = CASH_LOGS_COLUMNS;

/**
 * Cash Logs bills configuration:
 * * This is used to generate the Cash Logs bills column table
 */
export const CASH_LOGS_BILLS = [
  {
    id: 'one',
    label: '$1',
  },
  {
    id: 'two',
    label: '$2',
  },
  {
    id: 'five',
    label: '$5',
  },
  {
    id: 'ten',
    label: '$10',
  },
  {
    id: 'twenty',
    label: '$20',
  },
  {
    id: 'fifty',
    label: '$50',
  },
  {
    id: 'hundred',
    label: '$100',
  },
];

/**
 * Machines filters configuration
 * * MACHINES_FILTERS_CONFIG: Table filters configuration. You can learn more about the Theme Table filters here:
 * * https://github.com/bitstopco/bitstop-theme#live-docs
 */
const CURRENCY_FILTER_OPTIONS = [
  { value: 'USD', label: 'USD' },
  { value: 'CAD', label: 'CAD' },
];

export const MACHINES_FILTERS_CONFIG: TableToolbarFilterConfig[] = [
  {
    property: 'id',
    type: 'select-ids',
    placeholder: 'Machine',
    inputPlaceholder: 'Machine ID',
    label: 'Machine ID',
    validate: validateFilterById('machine'),
    mapperSelectedValue: (value: string) => `Machine ${value}`,
    mapperSelectedValues: (count: number) => `Machine ID (${count})`,
  },
  {
    type: 'select',
    property: 'currency',
    placeholder: 'Supported Currency',
    clearText: 'Clear filter',
    label: 'Supported Currency',
    options: CURRENCY_FILTER_OPTIONS,
  },
  {
    type: 'select',
    property: 'state',
    showFooter: true,
    hideApplyButton: true,
    placeholder: 'State',
    clearText: 'Clear filter',
    label: 'State',
    options: US_STATE_FILTER_OPTIONS,
    multiple: true,
  },
  {
    type: 'select',
    property: 'status',
    showFooter: true,
    hideApplyButton: true,
    placeholder: 'Status',
    clearText: 'Clear filter',
    label: 'Status',
    options: [],
    multiple: true,
  },
  {
    property: 'pick_up_id',
    label: 'Pick-up ID',
    type: 'select-ids',
    placeholder: 'Pick-up ID',
    inputPlaceholder: 'Pick-up ID',
    subtitleText: '',
    multiple: false,
    validate: (value: string) => {
      // We want to apply the custom filter for the pick_up_id validation
      const filters: ApiListingRequest = {
        filter: {
          operator: 'and',
          operands: [
            {
              operator: 'equal',
              property: 'pick_up_id',
              value: value,
            },
          ],
        },
        pagination: {
          current_page: 1,
          items_per_page: 1,
          sort_ascending: false,
          sort_by: 'id',
        },
      };

      return validateFilterById('pick_up_id')(filters);
    },
    mapperSelectedValue: (value: string) => `Machine ${value}`,
    mapperSelectedValues: (count: number) => `Machine ID (${count})`,
  },
  {
    property: 'tag_ids',
    operator: 'contains',
    type: 'select',
    label: 'Tags',
    placeholder: 'Tags',
    searchable: true,
    multiple: true,
    showFooter: true,
    options: [],
    searchPlaceholder: 'Search by tag name',
  },
  {
    property: 'last_restart',
    operator: 'less_than_or_equal',
    type: 'select-timeframe',
    label: 'Select a time frame',
    placeholder: 'Last Restart',
    showFooter: true,
    hideApplyButton: true,
    options: [...CONSTANTS.TIME_FRAME],
  },
  {
    property: 'last_seen',
    type: 'select-timeframe',
    label: 'Select a time frame',
    placeholder: 'Last Seen',
    showFooter: true,
    hideApplyButton: true,
    options: [...CONSTANTS.TIME_FRAME],
  },
  {
    // Synthetic property, not a real machine field -- rewritten into a real location_id
    // null-check by transformMachinesFilterPayload before the request goes out. See that
    // function for why this can't just filter on location_id directly.
    property: 'installed',
    type: 'select',
    showFooter: true,
    hideApplyButton: true,
    placeholder: 'Installed at Location',
    clearText: 'Clear filter',
    label: 'Installed at Location',
    options: [
      { label: 'Installed', value: 'installed' },
      { label: 'Not Installed', value: 'not_installed' },
    ],
  },
  {
    property: 'location_id',
    type: 'select-ids',
    placeholder: 'Location',
    inputPlaceholder: 'Location ID',
    label: 'Location ID',
    validate: (value: string) => {
      // getLocations is a POST listing endpoint -- it needs a full ApiListingRequest body,
      // not the bare id, or axios sends a raw string with no JSON content-type and athena
      // responds 415. LocationId is an int on athena's side and its JSON options don't allow
      // reading numbers from strings, so a quoted "5" fails to deserialize there -- send a
      // real number.
      const filters: ApiListingRequest = {
        filter: {
          operator: 'and',
          operands: [
            {
              operator: 'equal',
              property: 'location_id',
              value: Number(value),
            },
          ],
        },
        pagination: {
          current_page: 1,
          items_per_page: 1,
          sort_ascending: false,
          sort_by: 'location_id',
        },
      };

      return validateFilterById('location')(filters);
    },
    mapperSelectedValue: (value: string) => `Location ${value}`,
    mapperSelectedValues: (count: number) => `Location ID (${count})`,
  },
];

/**
 * Integration Machines filters configuration
 * * MACHINES_FILTERS_CONFIG: Table filters configuration. You can learn more about the Theme Table filters here:
 * * https://github.com/bitstopco/bitstop-theme#live-docs
 */
export const INTEGRATION_MACHINES_FILTERS_CONFIG: TableToolbarFilterConfig[] = [
  {
    property: 'id',
    type: 'select-ids',
    placeholder: 'Machine',
    inputPlaceholder: 'Machine ID',
    label: 'Machine ID',
    validate: validateFilterById('machine'),
    mapperSelectedValue: (value: string) => `Machine ${value}`,
    mapperSelectedValues: (count: number) => `Machine ID (${count})`,
  },
  {
    type: 'select',
    searchable: true,
    multiple: true,
    operator: 'equal',
    property: 'integration_id',
    showFooter: true,
    placeholder: 'Integration',
    clearText: 'Clear filter',
    label: 'Integration',
    options: [],
    searchPlaceholder: 'Search by integration name',
  },
  {
    type: 'select',
    property: 'currency',
    showFooter: true,
    hideApplyButton: true,
    placeholder: 'Supported Currency',
    clearText: 'Clear filter',
    label: 'Supported Currency',
    options: CURRENCY_FILTER_OPTIONS,
  },
  {
    type: 'select',
    property: 'state',
    showFooter: true,
    hideApplyButton: true,
    placeholder: 'State',
    clearText: 'Clear filter',
    label: 'State',
    options: US_STATE_FILTER_OPTIONS,
    multiple: true,
  },
  {
    property: 'tag_ids',
    operator: 'contains',
    type: 'select',
    label: 'Tags',
    placeholder: 'Tags',
    searchable: true,
    multiple: true,
    showFooter: true,
    options: [],
    searchPlaceholder: 'Search by tag name',
  },
  {
    property: 'last_seen',
    type: 'select-timeframe',
    label: 'Select a time frame',
    placeholder: 'Last Seen',
    showFooter: true,
    hideApplyButton: true,
    options: [...CONSTANTS.TIME_FRAME],
  },
];

/**
 * Machines command statuses used in the Machines Table column status component
 */
export const MACHINES_COMMAND_STATUSES: Record<
  string,
  {
    label: string;
    color: ChipProps['color'];
  }
> = {
  ok: {
    color: 'success',
    label: 'Ok',
  },
  maintenance_mode: {
    color: 'error',
    label: 'Maintenance Mode',
  },
};

export const MACHINES_COMMAND_STATUSES_VALUES = Object.keys(MACHINES_COMMAND_STATUSES).map(
  (key) => {
    const { label } = MACHINES_COMMAND_STATUSES[key];

    return {
      key,
      value: key,
      label: label,
    };
  },
);

export const MACHINES_MULTI_SELECT_CONFIG = [
  {
    label: 'Add Tag',
  },
  {
    label: 'Remove Tag',
  },
  // {
  //   label: 'Clear Cashboxes',
  // },
  {
    label: 'Restart Machines',
  },
  {
    label: 'Set Statuses',
  },
  {
    label: 'Enable Assets',
  },
  {
    label: 'Disable Assets',
  },
  {
    label: 'Create Case',
  },
];

export const INTEGRATION_MACHINES_MULTI_SELECT_CONFIG = [
  {
    label: 'Add Tag',
  },
  {
    label: 'Remove Tag',
  },
  {
    label: 'Enable Assets',
  },
  {
    label: 'Disable Assets',
  },
];

export const INSTALLED_MACHINES_COLUMNS: Columns<InstalledMachine> = {
  defaultValue: '-',
  labelSx: {
    whiteSpace: 'nowrap',
  },
  valueSx: {
    whiteSpace: 'nowrap',
  },
  items: [
    {
      label: 'Machine',
      valueMapper: (_, data) => <TableColumnMachine data={data as Machine} />,
      valueSx: {
        '& h6': {
          maxWidth: 400,
          whiteSpace: 'normal',
          wordWrap: 'break-word',
        },
      },
    },
    {
      value: 'status',
      label: 'Status',
      valueMapper: (value: Machine['status']) => value && <MachinesStatus value={value} />,
    },
    {
      value: 'serial',
      label: 'Serial',
    },
    {
      value: 'machine_id',
      label: 'Global ID',
    },
  ],
};
